import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { SlideOver } from '@/components/ui/SlideOver';
import { Button } from '@/components/ui/Button';
import { usePropertyEditorApi } from './api-context';
import { extractErrorMessage } from '@/lib/error';
import { showToast } from '@/lib/toast';
import type { PcUnit, UnitTypeValue } from '@/services/api/property-content.api';
import { UNIT_TYPES } from '@/services/api/property-content.api';
import { TextField, SelectField, CheckboxField } from './fields';

interface RoomFormSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  unit: PcUnit | null;
}

interface RoomForm {
  name: string;
  type: UnitTypeValue;
  capacity: string;
  baseOccupancy: string;
  extraPersonCharge: string;
  basePrice: string;
  weekendPrice: string;
  minimumStay: string;
  isActive: boolean;
}

function toForm(unit: PcUnit | null): RoomForm {
  return {
    name: unit?.name ?? '',
    type: unit?.type ?? 'studio',
    capacity: unit ? String(unit.capacity) : '2',
    baseOccupancy: unit?.baseOccupancy != null ? String(unit.baseOccupancy) : '',
    extraPersonCharge: unit?.extraPersonCharge ?? '',
    basePrice: unit?.basePrice ?? '',
    weekendPrice: unit?.weekendPrice ?? '',
    minimumStay: unit ? String(unit.minimumStay) : '1',
    isActive: unit ? unit.isActive : true,
  };
}

export function RoomFormSlideOver({ isOpen, onClose, propertyId, unit }: RoomFormSlideOverProps) {
  const [form, setForm] = useState<RoomForm>(() => toForm(unit));
  const [error, setError] = useState('');
  const { useCreateUnit, useUpdateUnit } = usePropertyEditorApi();
  const createMutation = useCreateUnit(propertyId);
  const updateMutation = useUpdateUnit(propertyId);
  const isEdit = !!unit;
  const pending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (isOpen) {
      setForm(toForm(unit));
      setError('');
    }
  }, [isOpen, unit]);

  const set = <K extends keyof RoomForm>(key: K, value: RoomForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const capacity = parseInt(form.capacity, 10);
  const basePrice = parseFloat(form.basePrice);
  const baseOccProvided = form.baseOccupancy.trim() !== '';
  const baseOccupancy = parseInt(form.baseOccupancy, 10);
  const extraProvided = form.extraPersonCharge.trim() !== '';
  const extraPersonCharge = parseFloat(form.extraPersonCharge);
  const baseOccValid =
    !baseOccProvided ||
    (Number.isInteger(baseOccupancy) && baseOccupancy >= 1 && baseOccupancy <= capacity);
  const extraValid = !extraProvided || (Number.isFinite(extraPersonCharge) && extraPersonCharge >= 0);
  const isValid =
    form.name.trim() !== '' &&
    Number.isInteger(capacity) &&
    capacity >= 1 &&
    Number.isFinite(basePrice) &&
    basePrice >= 0 &&
    baseOccValid &&
    extraValid;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!isValid) {
      setError(
        'Name, a capacity ≥ 1 and a base price are required. Base occupancy must be 1…capacity and the extra-person charge ≥ 0.',
      );
      return;
    }
    const weekend = form.weekendPrice.trim() === '' ? undefined : parseFloat(form.weekendPrice);
    const minStay = form.minimumStay.trim() === '' ? undefined : parseInt(form.minimumStay, 10);
    const baseOcc = baseOccProvided ? baseOccupancy : undefined;
    const extraPerson = extraProvided ? extraPersonCharge : undefined;
    try {
      if (isEdit && unit) {
        await updateMutation.mutateAsync({
          unitId: unit.id,
          body: {
            name: form.name.trim(),
            type: form.type,
            capacity,
            baseOccupancy: baseOcc,
            extraPersonCharge: extraPerson,
            basePrice,
            weekendPrice: weekend,
            minimumStay: minStay,
            isActive: form.isActive,
          },
        });
        showToast('Room updated · live on storefront');
      } else {
        await createMutation.mutateAsync({
          name: form.name.trim(),
          type: form.type,
          capacity,
          baseOccupancy: baseOcc,
          extraPersonCharge: extraPerson,
          basePrice,
          weekendPrice: weekend,
          minimumStay: minStay,
          isActive: form.isActive,
        });
        showToast('Room added');
      }
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <SlideOver isOpen={isOpen} onClose={() => !pending && onClose()} title={isEdit ? 'Edit room' : 'Add room'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert">
            {error}
          </div>
        )}

        <TextField id="room-name" label="Room name" value={form.name} onChange={(v) => set('name', v)} required maxLength={255} placeholder="e.g. Deluxe Studio" />
        <SelectField id="room-type" label="Type" value={form.type} onChange={(v) => set('type', v as UnitTypeValue)} options={UNIT_TYPES} required />
        <div className="grid grid-cols-2 gap-3">
          <TextField id="room-capacity" label="Capacity" value={form.capacity} onChange={(v) => set('capacity', v)} type="number" inputMode="numeric" required />
          <TextField id="room-min-stay" label="Minimum stay (nights)" value={form.minimumStay} onChange={(v) => set('minimumStay', v)} type="number" inputMode="numeric" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField id="room-base-price" label="Base price" value={form.basePrice} onChange={(v) => set('basePrice', v)} inputMode="decimal" required hint="Must be > 0 to count for publish" />
          <TextField id="room-weekend-price" label="Weekend price" value={form.weekendPrice} onChange={(v) => set('weekendPrice', v)} inputMode="decimal" hint="Optional" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField id="room-base-occupancy" label="Base occupancy (included in rate)" value={form.baseOccupancy} onChange={(v) => set('baseOccupancy', v)} type="number" inputMode="numeric" hint="Blank = same as capacity (no extra charge)" />
          <TextField id="room-extra-person-charge" label="Extra person charge / night" value={form.extraPersonCharge} onChange={(v) => set('extraPersonCharge', v)} inputMode="decimal" hint="Per extra guest, per night (0 = none)" />
        </div>
        <CheckboxField id="room-active" label="Active (bookable)" checked={form.isActive} onChange={(v) => set('isActive', v)} />

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" isLoading={pending} disabled={!isValid || pending}>
            <Save size={16} aria-hidden="true" />
            {isEdit ? 'Save room' : 'Add room'}
          </Button>
        </div>
      </form>
    </SlideOver>
  );
}
